import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../services/AxiosInstance';
import { saveTokenInLocalStorage, saveUserDataLocalStorage } from '../../services/AuthService';

/**
 * Restores a flowdo session when returning from an external module (e.g. Snap Bill).
 * The module passes a one-time handoff code; we exchange it for access + refresh tokens.
 */
export default function ModuleSessionCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    const code = params.get('code');
    if (!code) {
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await axiosInstance.post('/auth/module-handoff/return-exchange', { code });
        const data = res.data;
        if (!data?.accessToken || !data?.user) {
          throw new Error('Invalid session response');
        }

        const apiTimestamp = data.timestamp || Date.now();
        saveTokenInLocalStorage(data.accessToken, true, apiTimestamp);
        saveUserDataLocalStorage(data.user);

        if (cancelled) return;

        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath, { replace: true });
          return;
        }

        navigate('/', { replace: true });
      } catch (err) {
        console.error('Module session restore failed:', err);
        if (!cancelled) {
          navigate('/login', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, params]);

  return (
    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '40vh' }}>
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="text-muted mb-0">Restoring your Flowdo session…</p>
    </div>
  );
}
